import React, { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';

// ==================== 形状绘制工具（与原生版一致） ====================
const shapeUtils = {
  addShapePath(ctx, cx, cy, r, shape) {
    ctx.beginPath();
    switch (shape) {
      case 'circle':
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        break;
      case 'square':
        ctx.rect(cx - r, cy - r, r * 2, r * 2);
        break;
      case 'star': {
        const spikes = 5;
        const outerR = r;
        const innerR = r * 0.382;
        const step = Math.PI / spikes;
        const rot = (Math.PI / 2) * 3;
        for (let i = 0; i < spikes * 2; i++) {
          const curR = i % 2 === 0 ? outerR : innerR;
          const angle = rot + i * step;
          const x = cx + Math.cos(angle) * curR;
          const y = cy + Math.sin(angle) * curR;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        break;
      }
      case 'drop': {
        ctx.moveTo(cx, cy - r * 0.7);
        ctx.bezierCurveTo(
          cx + r * 0.8, cy - r * 0.3,
          cx + r * 0.8, cy + r * 0.5,
          cx, cy + r
        );
        ctx.bezierCurveTo(
          cx - r * 0.8, cy + r * 0.5,
          cx - r * 0.8, cy - r * 0.3,
          cx, cy - r * 0.7
        );
        ctx.closePath();
        break;
      }
      case 'clover': {
        const petalCount = 4;
        const petalLength = r * 0.8;
        const petalWidth = r * 0.5;
        for (let i = 0; i < petalCount; i++) {
          const angle = (i * Math.PI) / 2 - Math.PI / 4;
          const petalX = cx + Math.cos(angle) * r * 0.4;
          const petalY = cy + Math.sin(angle) * r * 0.4;
          ctx.save();
          ctx.translate(petalX, petalY);
          ctx.rotate(angle);
          ctx.moveTo(0, -petalLength);
          ctx.bezierCurveTo(
            petalWidth, -petalLength / 2,
            petalWidth, petalLength / 2,
            0, petalLength
          );
          ctx.bezierCurveTo(
            -petalWidth, petalLength / 2,
            -petalWidth, -petalLength / 2,
            0, -petalLength
          );
          ctx.closePath();
          ctx.restore();
        }
        ctx.arc(cx, cy, r * 0.2, 0, Math.PI * 2);
        break;
      }
      default:
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
    }
  },

  isPointInShape(cx, cy, r, shape, testX, testY) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = r * 2 + 6;
    canvas.width = size;
    canvas.height = size;
    const offsetX = r + 3 - cx;
    const offsetY = r + 3 - cy;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    this.addShapePath(ctx, cx + offsetX, cy + offsetY, r, shape);
    return ctx.isPointInPath(testX + offsetX, testY + offsetY);
  },
};

// ==================== 辅助函数 ====================
const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.9) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          const compressed = new Image();
          compressed.src = URL.createObjectURL(blob);
          compressed.onload = () => resolve(compressed);
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// 防抖
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// ==================== 主组件 ====================
const App = () => {
  // ---------- 状态 ----------
  const [originalImage, setOriginalImage] = useState(null);   // Image 对象
  const [points, setPoints] = useState([]);                   // 碎片数组
  const [historyStack, setHistoryStack] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [layout, setLayout] = useState('lr');                 // 'lr' 或 'tb'
  const [reverseLayout, setReverseLayout] = useState(false);
  const [dotSize, setDotSize] = useState(32);
  const [currentShape, setCurrentShape] = useState('circle');
  const [sampleSpacing, setSampleSpacing] = useState(22);

  // UI 辅助
  const [isDragging, setIsDragging] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  // Ref
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageInputRef = useRef(null);

  // 获取布局区域信息（原图区、纯色区坐标）
  const getLayoutAreas = useCallback(() => {
    if (!originalImage) return null;
    const w = originalImage.width;
    const h = originalImage.height;
    let imgX, imgY, solidX, solidY;
    if (layout === 'lr') {
      if (!reverseLayout) {
        imgX = 0; imgY = 0; solidX = w; solidY = 0;
      } else {
        imgX = w; imgY = 0; solidX = 0; solidY = 0;
      }
    } else {
      if (!reverseLayout) {
        imgX = 0; imgY = 0; solidX = 0; solidY = h;
      } else {
        imgX = 0; imgY = h; solidX = 0; solidY = 0;
      }
    }
    return { imgX, imgY, solidX, solidY, w, h };
  }, [originalImage, layout, reverseLayout]);

  // 渲染 canvas (核心绘图)
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImage) return;
    const ctx = canvas.getContext('2d');
    const areas = getLayoutAreas();
    if (!areas) return;
    const { imgX, imgY, solidX, solidY, w, h } = areas;

    // 设置画布尺寸
    if (layout === 'lr') {
      canvas.width = w * 2;
      canvas.height = h;
    } else {
      canvas.width = w;
      canvas.height = h * 2;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // 绘制原图
    ctx.drawImage(originalImage, imgX, imgY, w, h);
    // 绘制纯色背景区
    ctx.fillStyle = bgColor;
    ctx.fillRect(solidX, solidY, w, h);

    // 绘制所有碎片
    for (const p of points) {
      const { imgX: relImgX, imgY: relImgY, fragX, fragY, r } = p;

      // 原图区挖孔
      ctx.save();
      shapeUtils.addShapePath(ctx, imgX + relImgX, imgY + relImgY, r, currentShape);
      ctx.fillStyle = bgColor;
      ctx.fill();
      ctx.restore();

      // 纯色区绘制图像碎片
      const centerX = solidX + fragX;
      const centerY = solidY + fragY;
      ctx.save();
      shapeUtils.addShapePath(ctx, centerX, centerY, r, currentShape);
      ctx.clip();

      const srcX = relImgX - r;
      const srcY = relImgY - r;
      const srcW = r * 2;
      const srcH = r * 2;
      const dstX = centerX - r;
      const dstY = centerY - r;

      ctx.drawImage(
        originalImage,
        Math.max(0, srcX), Math.max(0, srcY),
        Math.min(srcW, originalImage.width - srcX),
        Math.min(srcH, originalImage.height - srcY),
        dstX + (srcX < 0 ? -srcX : 0),
        dstY + (srcY < 0 ? -srcY : 0),
        Math.min(srcW, originalImage.width - srcX),
        Math.min(srcH, originalImage.height - srcY)
      );
      ctx.restore();
    }
  }, [originalImage, points, bgColor, layout, reverseLayout, currentShape, getLayoutAreas]);

  // 历史记录管理
  const pushToHistory = useCallback((newPoints) => {
    const snapshot = newPoints.map(p => ({ ...p }));
    setHistoryStack(prev => {
      let newStack = prev.slice(0, historyIndex + 1);
      newStack.push(snapshot);
      if (newStack.length > 60) newStack.shift();
      return newStack;
    });
    setHistoryIndex(prev => {
      let idx = prev + 1;
      if (idx >= historyStack.length + 1) idx = historyStack.length;
      return idx;
    });
  }, [historyIndex, historyStack.length]);

  // 撤销
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const prevPoints = historyStack[historyIndex - 1];
      setPoints(prevPoints.map(p => ({ ...p })));
      setHistoryIndex(historyIndex - 1);
    }
  }, [historyIndex, historyStack]);

  // 清空所有碎片
  const clearAll = useCallback(() => {
    if (points.length === 0) return;
    const newPoints = [];
    setPoints(newPoints);
    pushToHistory(newPoints);
  }, [points, pushToHistory]);

  // 添加新碎片（带采样间距限制）
  const addPoint = useCallback((imgX, imgY, r) => {
    if (!originalImage) return;
    const w = originalImage.width;
    const h = originalImage.height;
    const clampedX = clamp(imgX, 0, w);
    const clampedY = clamp(imgY, 0, h);
    const radiusBased = r * 0.35;
    const minDist = Math.max(sampleSpacing, radiusBased);

    // 检查与现有碎片的距离
    for (const p of points) {
      const dx = p.imgX - clampedX;
      const dy = p.imgY - clampedY;
      if (Math.hypot(dx, dy) < minDist) return;
    }

    const newPoint = {
      imgX: clampedX,
      imgY: clampedY,
      fragX: clampedX,
      fragY: clampedY,
      r: r,
    };
    const newPoints = [...points, newPoint];
    setPoints(newPoints);
    pushToHistory(newPoints);
  }, [originalImage, points, sampleSpacing, pushToHistory]);

  // 更新碎片位置（拖拽）
  const updateFragmentPosition = useCallback((index, newFragX, newFragY) => {
    if (!originalImage) return false;
    const w = originalImage.width;
    const h = originalImage.height;
    const clampedX = clamp(newFragX, 0, w);
    const clampedY = clamp(newFragY, 0, h);
    const point = points[index];
    if (point.fragX === clampedX && point.fragY === clampedY) return false;
    const newPoints = [...points];
    newPoints[index] = { ...point, fragX: clampedX, fragY: clampedY };
    setPoints(newPoints);
    pushToHistory(newPoints);
    return true;
  }, [originalImage, points, pushToHistory]);

  // 坐标转换：canvas 绝对坐标 → 相对坐标（原图区或纯色区）
  const getRelativeCoords = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImage) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    if (canvasX < 0 || canvasX > canvas.width || canvasY < 0 || canvasY > canvas.height) return null;

    const areas = getLayoutAreas();
    if (!areas) return null;
    const { imgX, imgY, solidX, solidY, w, h } = areas;

    if (canvasX >= imgX && canvasX <= imgX + w && canvasY >= imgY && canvasY <= imgY + h) {
      return { type: 'img', relX: canvasX - imgX, relY: canvasY - imgY };
    }
    if (canvasX >= solidX && canvasX <= solidX + w && canvasY >= solidY && canvasY <= solidY + h) {
      return { type: 'solid', relX: canvasX - solidX, relY: canvasY - solidY };
    }
    return null;
  }, [originalImage, getLayoutAreas]);

  // 检测是否命中碎片（用于拖拽）
  const hitTest = useCallback((clientX, clientY) => {
    if (!originalImage || points.length === 0) return -1;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    const areas = getLayoutAreas();
    if (!areas) return -1;
    const { solidX, solidY } = areas;
    for (let i = points.length - 1; i >= 0; i--) {
      const p = points[i];
      const centerX = solidX + p.fragX;
      const centerY = solidY + p.fragY;
      if (shapeUtils.isPointInShape(centerX, centerY, p.r, currentShape, canvasX, canvasY)) {
        return i;
      }
    }
    return -1;
  }, [originalImage, points, getLayoutAreas, currentShape]);

  // ---------- 交互事件 ----------
  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    if (!originalImage) return;
    const clientX = e.clientX ?? (e.touches ? e.touches[0].clientX : 0);
    const clientY = e.clientY ?? (e.touches ? e.touches[0].clientY : 0);
    const hit = hitTest(clientX, clientY);
    if (hit !== -1) {
      setIsDragging(true);
      setDraggingIndex(hit);
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
      return;
    }
    const coord = getRelativeCoords(clientX, clientY);
    if (coord && coord.type === 'img') {
      setIsAdding(true);
      addPoint(coord.relX, coord.relY, dotSize);
    }
  }, [originalImage, hitTest, getRelativeCoords, addPoint, dotSize]);

  const onPointerMove = useCallback((e) => {
    if (!originalImage) return;
    const clientX = e.clientX ?? (e.touches ? e.touches[0].clientX : 0);
    const clientY = e.clientY ?? (e.touches ? e.touches[0].clientY : 0);
    if (isDragging && draggingIndex !== -1) {
      e.preventDefault();
      const coord = getRelativeCoords(clientX, clientY);
      if (coord && coord.type === 'solid') {
        updateFragmentPosition(draggingIndex, coord.relX, coord.relY);
      }
      return;
    }
    if (isAdding && !isDragging) {
      const coord = getRelativeCoords(clientX, clientY);
      if (coord && coord.type === 'img') {
        addPoint(coord.relX, coord.relY, dotSize);
      }
    }
  }, [originalImage, isDragging, draggingIndex, isAdding, getRelativeCoords, updateFragmentPosition, addPoint, dotSize]);

  const onPointerUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDraggingIndex(-1);
      if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
    }
    setIsAdding(false);
  }, [isDragging]);

  // 鼠标移入移出改变样式
  const onCanvasMouseMove = useCallback((e) => {
    if (!originalImage) return;
    const hit = hitTest(e.clientX, e.clientY);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = hit !== -1 ? 'grab' : 'crosshair';
    }
  }, [originalImage, hitTest]);

  const onCanvasMouseLeave = useCallback(() => {
    if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
  }, []);

  // 下载成品
  const downloadCanvas = useCallback(() => {
    if (!originalImage) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = canvas.width * 2;
      tempCanvas.height = canvas.height * 2;
      tempCtx.scale(2, 2);
      tempCtx.drawImage(canvas, 0, 0);
      const link = document.createElement('a');
      link.download = `拼贴生成器_${Date.now()}.png`;
      link.href = tempCanvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      const link = document.createElement('a');
      link.download = `拼贴生成器_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  }, [originalImage]);

  // 图片上传
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const img = await compressImage(file);
      setOriginalImage(img);
      setPoints([]);
      setHistoryStack([]);
      setHistoryIndex(-1);
      // 清空后推一个空历史
      setTimeout(() => {
        pushToHistory([]);
      }, 0);
    } catch (err) {
      alert('图片加载失败');
    } finally {
      setLoading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // 当依赖变化时重新绘制 canvas
  useEffect(() => {
    if (originalImage) {
      renderCanvas();
    }
  }, [originalImage, points, bgColor, layout, reverseLayout, currentShape, renderCanvas]);

  // 添加键盘快捷键
  useEffect(() => {
    const handleKey = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            undo();
            break;
          case 'r':
            e.preventDefault();
            clearAll();
            break;
          case 's':
            e.preventDefault();
            downloadCanvas();
            break;
          default:
            break;
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [undo, clearAll, downloadCanvas]);

  // 绑定画布事件
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('mousedown', onPointerDown);
    canvas.addEventListener('touchstart', onPointerDown, { passive: false });
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
    canvas.addEventListener('mousemove', onCanvasMouseMove);
    canvas.addEventListener('mouseleave', onCanvasMouseLeave);
    return () => {
      canvas.removeEventListener('mousedown', onPointerDown);
      canvas.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
      canvas.removeEventListener('mousemove', onCanvasMouseMove);
      canvas.removeEventListener('mouseleave', onCanvasMouseLeave);
    };
  }, [onPointerDown, onPointerMove, onPointerUp, onCanvasMouseMove, onCanvasMouseLeave]);

  return (
    <div className="app">
      <h1>拼贴生成器</h1>

      <div className="controls">
        <div className="control-group">
          <label className="file-upload">
             上传照片
            <input type="file" ref={imageInputRef} accept="image/*" onChange={handleImageUpload} />
          </label>
        </div>

        <div className="control-group">
          <label> 背景色:</label>
          <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
        </div>

        <div className="control-group">
          <label> 布局:</label>
          <select value={layout} onChange={(e) => setLayout(e.target.value)}>
            <option value="lr">左右拼接</option>
            <option value="tb">上下拼接</option>
          </select>
        </div>

        <div className="control-group">
          <label> 反转区域:</label>
          <input type="checkbox" checked={reverseLayout} onChange={(e) => setReverseLayout(e.target.checked)} />
        </div>

        <div className="control-group">
          <label> 图形大小:</label>
          <input type="range" min="12" max="100" value={dotSize} onChange={(e) => setDotSize(Number(e.target.value))} />
          <span>{dotSize}px</span>
        </div>

        <div className="control-group">
          <label> 形状选择:</label>
          <select value={currentShape} onChange={(e) => setCurrentShape(e.target.value)}>
            <option value="circle">圆形</option>
            <option value="square">方形</option>
            <option value="star">五角星形</option>
            <option value="drop">水滴形</option>
            <option value="clover">十字星形</option>
          </select>
        </div>

        <div className="control-group">
          <label> 采样间距:</label>
          <input type="range" min="8" max="70" value={sampleSpacing} onChange={(e) => setSampleSpacing(Number(e.target.value))} step="1" />
          <span>{sampleSpacing}px</span>
        </div>

        <div className="control-group">
          <button className="btn-secondary" onClick={undo} title="撤销 (Ctrl+Z)">↩ 撤销</button>
          <button className="btn-secondary" onClick={clearAll} title="清空 (Ctrl+R)">🗑 清空</button>
          <button className="btn-primary" onClick={downloadCanvas} title="下载 (Ctrl+S)">⬇ 下载成品</button>
        </div>
      </div>

      <div className="canvas-container" ref={containerRef}>
        {!originalImage && <div className="placeholder-text"> 上传照片，点击/拖动照片区域生成拼贴碎片<br /> 拖拽右侧/下方纯色区的碎片即可移动</div>}
        {loading && <div className="loading"> 加载并压缩图片中...</div>}
        <canvas ref={canvasRef} style={{ display: originalImage ? 'block' : 'none' }} />
      </div>

    </div>
  );
};

export default App;